import { useState } from 'react';
import { Building2, ChevronRight } from 'lucide-react';
import { Chip } from '@/ui/primitives';
import { NIVEL_LABEL, filterVerificacoes, nivelTone, sortVerificacoes } from '../data';
import type { ImovelDetail, OnrItem, Verification } from '../schemas';
import { VerificationRow } from './VerificationRow';

interface Props {
  imovel: ImovelDetail;
  cpf: string;
  onr?: OnrItem | null;
}

// Mapa do código de status ONR pro status canônico do diagnóstico.
const ONR_STATUS_TO_VERIFICATION_STATUS: Record<string, string> = {
  AT: 'regular', // Ativo
  PE: 'pendente', // Pendente
  SU: 'atencao', // Suspenso
  CA: 'irregular', // Cancelado
};

const ONR_STATUS_LABEL: Record<string, string> = {
  AT: 'Ativo',
  PE: 'Pendente',
  SU: 'Suspenso',
  CA: 'Cancelado',
};

// Converte o item ONR numa verificação sintética que se mistura com as outras
// (ANM, CCIR, CNFP, etc.) e renderiza no mesmo VerificationRow.
function onrToVerification(item: OnrItem): Verification {
  const onr = item.onr;
  if (item.erro) {
    return {
      verificacao: 'registro_imoveis',
      status: 'pendente',
      detalhe: item.erro,
      oportunidade: null,
      fonte_url: 'https://mapa.onr.org.br/',
    };
  }
  if (!onr) {
    return {
      verificacao: 'registro_imoveis',
      status: 'pendente',
      detalhe: 'Sem registro encontrado no ONR para este imóvel.',
      oportunidade: 'Verificar matrícula no portal ONR',
      fonte_url: 'https://mapa.onr.org.br/',
    };
  }
  const status = ONR_STATUS_TO_VERIFICATION_STATUS[onr.ind_status ?? ''] ?? 'pendente';
  const statusLabel = onr.ind_status ? (ONR_STATUS_LABEL[onr.ind_status] ?? onr.ind_status) : null;
  const partes = [
    statusLabel,
    onr.des_condic,
    onr.num_area != null
      ? `${onr.num_area.toLocaleString('pt-BR')} ha`
      : null,
    onr.dat_atuali ? `atualizado ${onr.dat_atuali}` : null,
    onr.cod_imovel ? `Código ONR ${onr.cod_imovel}` : null,
  ].filter(Boolean);
  return {
    verificacao: 'registro_imoveis',
    status,
    detalhe: partes.join(' · ') || null,
    oportunidade: null,
    fonte_url: 'https://mapa.onr.org.br/',
  };
}

// Card colapsável estilo "ver mais" — mesmo padrão dos accordions do ImovelDetailModal
// (icon-box tonalizado + título + subtítulo + chip de nível + chevron rotativo).
export function ImovelCard({ imovel, cpf, onr }: Props) {
  const [open, setOpen] = useState(false);
  const score = imovel.score ?? 0;
  const nivel = imovel.nivel ?? 'regular';
  const tone = nivelTone(nivel);
  const subtitle = [
    imovel.municipio,
    imovel.car_codigo ? `CAR ${imovel.car_codigo}` : 'Sem CAR',
  ]
    .filter(Boolean)
    .join(' · ');

  const toggle = () => setOpen((v) => !v);

  return (
    <div className="border border-border rounded-xl bg-elevated">
      <div
        className="flex items-center gap-3 px-3 py-3 cursor-pointer"
        onClick={toggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <span
          className="shrink-0 inline-flex items-center justify-center rounded-lg"
          style={{
            width: 36,
            height: 36,
            background: 'rgba(217,119,87,0.10)',
            color: '#d97757',
          }}
        >
          <Building2 size={18} strokeWidth={1.75} />
        </span>
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-sm font-bold text-text leading-tight truncate">
            {imovel.denominacao || `Imóvel #${imovel.imovel_id}`}
          </span>
          {subtitle && (
            <span className="text-xs text-text-muted truncate">{subtitle}</span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs font-semibold tabular-nums text-text">
            {score.toFixed(0)}%
          </span>
          <Chip tone={tone} size="sm" dot>
            {NIVEL_LABEL[nivel]}
          </Chip>
          <span className="text-xs text-text-muted hidden sm:inline">
            {open ? 'Mostrar menos' : 'Mostrar mais'}
          </span>
          <ChevronRight
            size={16}
            strokeWidth={1.5}
            className="text-text-muted transition-transform"
            style={{ transform: open ? 'rotate(90deg)' : undefined }}
          />
        </div>
      </div>

      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-border">
          {(() => {
            // Mescla a checagem ONR (sintética) com as outras verificações antes
            // de ordenar — fica na ordem alfabética junto com ANM, CCIR, CNFP etc.
            const all = filterVerificacoes(
              onr ? [...imovel.verificacoes, onrToVerification(onr)] : imovel.verificacoes,
            );
            if (all.length === 0) {
              return <p className="text-xs text-text-muted py-2">Sem verificações registradas.</p>;
            }
            return (
              <ul className="flex flex-col">
                {sortVerificacoes(all).map((v, i) => (
                  <VerificationRow
                    key={`${v.verificacao}-${i}`}
                    verification={v}
                    cpf={cpf}
                    imovelId={imovel.imovel_id}
                  />
                ))}
              </ul>
            );
          })()}
        </div>
      )}
    </div>
  );
}
